import React, { useContext } from 'react'
import { Col, Row } from 'react-grid-system'
import { useTranslation } from 'react-i18next'
import graphql from 'babel-plugin-relay/macro'
import { ErrorBoundary } from 'react-error-boundary'

import { ServiceCard } from 'pyrexx-react-library'
import StatusFilterButton from '../Status/StatusFilterButton'
import WithSkeleton from './WithSkeleton'
import SimpleLink from '../Simple/SimpleLink'
import QueryRendererContainer from '../QueryRendererContainer'
import NotAvailableContent from './NotAvailableContent'

import { Routes } from '../../configs/RouteConfig'
import UserContext from '../../helper/userContext'
import {
  filterDone,
  filterPending,
} from '../../constants/appointmentFilterModels'
import handleLocalStorage from '../../helper/handleLocalStorage'

import appointmentImg from '../../assets/overview/appointment.png'
import appointmentInactiveImg from '../../assets/overview/appointment-inactive.png'
import getDefaultDateFilter from '../../helper/getDefaultDateFilter'

const AppointmentsCardContent = (props) => {
  const { data = {}, loading = false } = props
  const { redirectRoute } = useContext(UserContext)
  const { t } = useTranslation()

  const handleFilterChange = (filter) => {
    handleLocalStorage('set', 'appointmentsFilter', filter)
    redirectRoute(Routes.appointments)
  }

  return (
    <Row align='start' style={{ width: '100%' }}>
      <Col align='center'>
        <WithSkeleton variant='lg' loading={loading}>
          <p style={{ marginTop: '0' }}>{t('PENDING')}</p>
        </WithSkeleton>

        {/* TODO implement that P status */}
        <WithSkeleton loading={loading}>
          <Row justify='center'>
            <StatusFilterButton
              onClick={() =>
                handleFilterChange({
                  filterId: 'box-pending',
                  filterModel: filterPending,
                })
              }
              status='warning'
              label={data?.pending}
              border={false}
            />
          </Row>
        </WithSkeleton>
      </Col>

      <Col align='center'>
        <WithSkeleton variant='lg' loading={loading}>
          <p style={{ marginTop: '0' }}>{t('EXECUTED')}</p>
        </WithSkeleton>

        <WithSkeleton loading={loading}>
          <Row justify='center'>
            <StatusFilterButton
              onClick={() =>
                handleFilterChange({
                  filterId: 'box-done',
                  filterModel: filterDone,
                })
              }
              status='success'
              label={data?.done}
              border={false}
            />
          </Row>
        </WithSkeleton>
      </Col>
    </Row>
  )
}

const AppointmentsCard = ({ data = {}, loading = false, error = false }) => {
  const { currentLanguage } = useContext(UserContext)
  const { t } = useTranslation()

  if (error) {
    return (
      <ServiceCard
        imgSrc={appointmentInactiveImg}
        title={t('APPOINTMENTS')}
        content={<NotAvailableContent imgSrc={appointmentInactiveImg} />}
        isDisabled
      />
    )
  }

  return (
    <ServiceCard
      imgSrc={appointmentImg}
      title={t('APPOINTMENTS')}
      content={<AppointmentsCardContent data={data} loading={loading} />}
      link={
        !loading && (
          <SimpleLink
            to={'/' + currentLanguage + Routes.appointments}
            font='quicksand'
          >
            {t('ALL') + ' >'}
          </SimpleLink>
        )
      }
    />
  )
}

const query = graphql`
  query AppointmentsCardQuery($companyId: ID!, $dateFilter: [Condition]!) {
    Me {
      appointmentsStatus: Company(id: $companyId) {
        done: countAppointments(
          options: {
            filters: [
              {
                name: "status"
                filterType: text
                operator: AND
                conditions: [
                  { value: ["DONE", "CLOSED", "CHECKED"], type: equals }
                ]
              }
              {
                name: "date"
                filterType: date
                operator: AND
                conditions: $dateFilter
              }
            ]
          }
        )
        pending: countAppointments(
          options: {
            filters: [
              {
                name: "status"
                filterType: text
                operator: AND
                conditions: [
                  { value: ["OPEN", "DIAGNOSE_PENDING"], type: equals }
                ]
              }
              {
                name: "date"
                filterType: date
                operator: AND
                conditions: $dateFilter
              }
            ]
          }
        )
      }
    }
  }
`

export default ({ isHidden = true }) => {
  const { user } = useContext(UserContext)

  const defaultDateFilter = getDefaultDateFilter().value

  const variables = {
    companyId: user.companyId,
    dateFilter: [{ value: defaultDateFilter, type: 'inRange' }],
  }

  if (isHidden) {
    return null
  }

  return (
    <ErrorBoundary fallbackRender={() => null}>
      <QueryRendererContainer
        query={query}
        variables={variables}
        loading={<AppointmentsCard loading />}
        render={(data) => {
          const appointmentsStatus = data?.Me?.appointmentsStatus

          return <AppointmentsCard data={appointmentsStatus} />
        }}
      />
    </ErrorBoundary>
  )
}
